import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { LogOut, Menu, ShieldCheck, Wrench, X } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { navFor, type NavItem } from '@/app/roleNav';
import { SessionExpiryModal } from '@/auth/SessionExpiryModal';
import { titleCase } from '@/lib/format';
import { cn } from '@/lib/cn';
import { ActiveCrashBanner } from '@/features/crashes/ActiveCrashBanner';

function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-[--radius-control] px-2.5 py-2 text-[13px] font-medium transition-colors',
                isActive
                  ? 'bg-accent-soft text-accent'
                  : 'text-ink-secondary hover:bg-surface-sunken hover:text-ink'
              )
            }
          >
            <item.icon className="size-4 shrink-0" aria-hidden />
            {item.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { user, signOut } = useAuth();
  const role = user?.role ?? null;
  const main = navFor(role, 'main');

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2 border-b border-line px-4">
        <ShieldCheck className="size-5 text-accent" aria-hidden />
        <span className="font-semibold tracking-tight text-ink">AutoAware</span>
      </div>

      <nav className="flex-1 overflow-y-auto p-3" aria-label="Main">
        <NavList items={main} onNavigate={onNavigate} />

        {/* Administration is its own console at /admin, not a section here — see
            ConsoleShell. This link is the way back into it. */}
        {role === 'administrator' && (
          <Link
            to="/admin"
            onClick={onNavigate}
            className="mt-5 flex items-center gap-2.5 rounded-[--radius-control] border border-line px-2.5 py-2 text-[13px] font-medium text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-ink"
          >
            <Wrench className="size-4 shrink-0" aria-hidden />
            Operations console
          </Link>
        )}
      </nav>

      <div className="border-t border-line p-3">
        <div className="px-2.5 pb-2">
          <p className="truncate text-[13px] font-medium text-ink">{user?.name}</p>
          <p className="truncate text-[12px] text-ink-muted">
            {role ? titleCase(role) : ''}
          </p>
        </div>
        <button
          onClick={signOut}
          className="flex w-full items-center gap-2.5 rounded-[--radius-control] px-2.5 py-2 text-[13px] font-medium text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-ink"
        >
          <LogOut className="size-4" aria-hidden />
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * One shell for every signed-in route. The previous app pasted its sidebar into
 * each page separately, which is why nothing was ever responsive: there was no
 * single place to make it so.
 */
export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-canvas">
      <aside className="fixed inset-y-0 left-0 hidden w-60 border-r border-line bg-surface lg:block">
        <SidebarContent />
      </aside>

      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-surface px-4 lg:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded p-1.5 text-ink-secondary hover:bg-surface-sunken"
          aria-label="Open navigation"
        >
          <Menu className="size-5" />
        </button>
        <Link to="/" className="flex items-center gap-2">
          <ShieldCheck className="size-5 text-accent" aria-hidden />
          <span className="font-semibold tracking-tight text-ink">AutoAware</span>
        </Link>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-ink/20"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          />
          <div className="absolute inset-y-0 left-0 w-72 bg-surface shadow-lg">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 rounded p-1 text-ink-muted hover:bg-surface-sunken"
              aria-label="Close navigation"
            >
              <X className="size-4" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <main className="lg:pl-60">
        <ActiveCrashBanner />
        <div key={location.pathname} className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      <SessionExpiryModal />
    </div>
  );
}
